import React, { useState } from 'react';
import { CartNotificationData } from '../../types/common';
import { formatCurrency, getCartData, formatCartItems, calculateTotalItems, calculateTotalPrice } from '../../utils/cartHelpers';
import { createLogger } from '../../utils/logger';
import './CartNotification.css';

const logger = createLogger('CartNotification');

interface CartNotificationProps extends CartNotificationData {
  onViewCart: () => void;
  onCheckout: () => void;
  onClose: () => void;
  userId?: string;
  supermarketId?: string;
  onRefresh?: (freshData: CartNotificationData) => void;
}

export const CartNotification: React.FC<CartNotificationProps> = ({
  addedItem,
  cartItems,
  totalItems,
  totalPrice,
  supermarketName,
  onViewCart,
  onCheckout,
  onClose,
  userId,
  supermarketId,
  onRefresh
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentData, setCurrentData] = useState({
    cartItems,
    totalItems,
    totalPrice
  });

  const handleRefresh = async () => {
    if (!userId || !supermarketId || !onRefresh) return;
    
    setIsRefreshing(true);
    try {
      const freshCartData = await getCartData(userId, supermarketId);
      const freshCartItems = formatCartItems(freshCartData);
      const freshTotalItems = calculateTotalItems(freshCartData);
      const freshTotalPrice = calculateTotalPrice(freshCartData);
      
      const freshData = {
        addedItem,
        cartItems: freshCartItems,
        totalItems: freshTotalItems,
        totalPrice: freshTotalPrice,
        supermarketName
      };
      
      setCurrentData({
        cartItems: freshCartItems,
        totalItems: freshTotalItems,
        totalPrice: freshTotalPrice
      });
      
      onRefresh(freshData);
    } catch (error) {
      logger.error('Error refreshing cart data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };
  return (
    <div className="cart-notification">
      {/* Header */}
      <div className="cart-notification-header">
        <span>✓ Item Added to Cart</span>
        <div className="cart-notification-actions">
          {onRefresh && userId && supermarketId && (
            <button 
              className="cart-notification-refresh"
              onClick={handleRefresh}
              disabled={isRefreshing}
              aria-label="Refresh cart data"
              title="Refresh cart data"
            >
              {isRefreshing ? '⟳' : '↻'}
            </button>
          )}
          <button 
            className="cart-notification-close"
            onClick={onClose}
            aria-label="Close notification"
          >
            ×
          </button>
        </div>
      </div>

      {/* Added Item Highlight */}
      <div className="cart-notification-added-item">
        <div className="added-item-name">{addedItem.name}</div>
        <div className="added-item-details">
          Quantity: {addedItem.quantity} • {formatCurrency(addedItem.price)}
        </div>
      </div>

      {/* Cart Items List */}
      {currentData.cartItems.length > 0 && (
        <>
          <div className="cart-notification-divider"></div>
          <div className="cart-notification-items">
            <div className="cart-items-header">
              Your {supermarketName ? `${supermarketName} ` : ''}Cart:
            </div>
            {currentData.cartItems.map((item, index) => (
              <div key={index} className="cart-notification-item">
                <span className="item-name">{item.name}</span>
                <span className="item-details">
                  {item.quantity} × {formatCurrency(item.price)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Cart Summary */}
      <div className="cart-notification-summary">
        <div className="summary-row">
          <span>Total Items:</span>
          <span>{currentData.totalItems}</span>
        </div>
        <div className="summary-row">
          <span>Total Price:</span>
          <span>{formatCurrency(currentData.totalPrice)}</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="cart-notification-buttons">
        <button 
          className="cart-notification-button cart-notification-button-secondary"
          onClick={onViewCart}
        >
          View Cart
        </button>
        <button 
          className="cart-notification-button cart-notification-button-primary"
          onClick={onCheckout}
        >
          Checkout
        </button>
      </div>
    </div>
  );
};

export default CartNotification;
